/**
 * ObjectPool — generic object pool to reduce GC pressure.
 *
 * Usage:
 *   const pool = new ObjectPool(() => scene.add.image(0, 0, 'fx_spark'), 30);
 *   const img = pool.acquire();    // reuse or create
 *   // ... use img ...
 *   pool.release(img);             // return to pool (caller must hide/reset it)
 */
export class ObjectPool<T> {
  private pool: T[] = [];
  private factory: () => T;

  constructor(factory: () => T, prewarmCount = 0) {
    this.factory = factory;
    for (let i = 0; i < prewarmCount; i++) {
      this.pool.push(this.factory());
    }
  }

  /** Get an object from the pool (or create a new one). */
  acquire(): T {
    return this.pool.length > 0 ? this.pool.pop()! : this.factory();
  }

  /** Return an object to the pool. Caller must deactivate/hide it first. */
  release(obj: T): void {
    this.pool.push(obj);
  }

  get size(): number { return this.pool.length; }

  /** Destroy all pooled objects using the provided destructor. */
  clear(destructor?: (obj: T) => void): void {
    if (destructor) {
      for (const obj of this.pool) destructor(obj);
    }
    this.pool.length = 0;
  }
}

// ── VFX Pool Manager ──
// Centralized pools for the most frequently created/destroyed Phaser GameObjects.
// Accessed globally via VFXPool singleton — initialized once per GameScene.

import Phaser from 'phaser';

let imagePool: ObjectPool<Phaser.GameObjects.Image> | null = null;
let textPool: ObjectPool<Phaser.GameObjects.Text> | null = null;
let graphicsPool: ObjectPool<Phaser.GameObjects.Graphics> | null = null;
let circlePool: ObjectPool<Phaser.GameObjects.Arc> | null = null;
let poolScene: Phaser.Scene | null = null;

export const VFXPool = {
  /** Call once in GameScene.create() to initialize all sub-pools. */
  init(scene: Phaser.Scene): void {
    poolScene = scene;

    imagePool = new ObjectPool(() => {
      const img = scene.add.image(-999, -999, '__DEFAULT');
      img.setVisible(false).setActive(false);
      return img;
    }, 40);
    // Pre-hide all prewarmed images
    for (let i = 0; i < 40; i++) {
      const img = imagePool.acquire();
      img.setVisible(false).setActive(false);
      imagePool.release(img);
    }

    textPool = new ObjectPool(() => {
      const txt = scene.add.text(-999, -999, '', {
        fontFamily: 'monospace', fontSize: '12px', fontStyle: 'bold',
        color: '#ffffff', stroke: '#000000', strokeThickness: 3,
      });
      txt.setVisible(false).setActive(false);
      return txt;
    }, 20);
    for (let i = 0; i < 20; i++) {
      const txt = textPool.acquire();
      txt.setVisible(false).setActive(false);
      textPool.release(txt);
    }

    graphicsPool = new ObjectPool(() => {
      const gfx = scene.add.graphics();
      gfx.setVisible(false).setActive(false);
      return gfx;
    }, 5);

    circlePool = new ObjectPool(() => {
      const c = scene.add.circle(-999, -999, 4, 0xffffff, 1);
      c.setVisible(false).setActive(false);
      return c;
    }, 10);
  },

  // ── Image pool ──

  /** Acquire an image, set texture, position, and make visible. */
  acquireImage(x: number, y: number, texture: string): Phaser.GameObjects.Image {
    const img = imagePool!.acquire();
    img.setTexture(texture);
    img.setPosition(x, y);
    img.setVisible(true).setActive(true);
    img.setAlpha(1).setScale(1).setAngle(0).setTint(0xffffff);
    img.setOrigin(0.5, 0.5);
    return img;
  },

  releaseImage(img: Phaser.GameObjects.Image): void {
    // Stop any tweens targeting this image
    poolScene?.tweens.killTweensOf(img);
    img.setVisible(false).setActive(false);
    img.setPosition(-999, -999);
    imagePool!.release(img);
  },

  // ── Text pool ──

  acquireText(x: number, y: number, content: string, style?: Partial<Phaser.Types.GameObjects.Text.TextStyle>): Phaser.GameObjects.Text {
    const txt = textPool!.acquire();
    txt.setPosition(x, y);
    txt.setText(content);
    if (style) {
      txt.setStyle(style);
    }
    txt.setVisible(true).setActive(true);
    txt.setAlpha(1).setScale(1).setAngle(0);
    txt.setOrigin(0.5, 1);
    return txt;
  },

  releaseText(txt: Phaser.GameObjects.Text): void {
    poolScene?.tweens.killTweensOf(txt);
    txt.setVisible(false).setActive(false);
    txt.setPosition(-999, -999);
    textPool!.release(txt);
  },

  // ── Graphics pool ──

  acquireGraphics(): Phaser.GameObjects.Graphics {
    const gfx = graphicsPool!.acquire();
    gfx.clear();
    gfx.setVisible(true).setActive(true);
    gfx.setAlpha(1).setPosition(0, 0);
    return gfx;
  },

  releaseGraphics(gfx: Phaser.GameObjects.Graphics): void {
    poolScene?.tweens.killTweensOf(gfx);
    gfx.clear();
    gfx.setVisible(false).setActive(false);
    graphicsPool!.release(gfx);
  },

  // ── Circle pool ──

  acquireCircle(x: number, y: number, radius: number, color: number, alpha: number): Phaser.GameObjects.Arc {
    const c = circlePool!.acquire();
    c.setPosition(x, y);
    c.setRadius(radius);
    c.setFillStyle(color, alpha);
    c.setVisible(true).setActive(true);
    c.setAlpha(1).setScale(1);
    return c;
  },

  releaseCircle(c: Phaser.GameObjects.Arc): void {
    poolScene?.tweens.killTweensOf(c);
    c.setVisible(false).setActive(false);
    c.setPosition(-999, -999);
    circlePool!.release(c);
  },

  /** Cleanup all pools — call in GameScene.shutdown(). */
  shutdown(): void {
    imagePool?.clear(img => img.destroy());
    textPool?.clear(txt => txt.destroy());
    graphicsPool?.clear(gfx => gfx.destroy());
    circlePool?.clear(c => c.destroy());
    imagePool = null;
    textPool = null;
    graphicsPool = null;
    circlePool = null;
    poolScene = null;
  },

  /** Whether the pool system is initialized. */
  get ready(): boolean { return imagePool !== null; },
};
